// src/components/shared/SearchableSelect.js
//
// A drop-in replacement for components/shared/Select when the option list is
// long enough to want type-to-filter. Wraps react-select (already a dependency)
// with the app's dark theme applied to EVERY part of the widget — the existing
// CustomSelect only themes `control`, which leaves react-select's default white
// menu and dark option text unreadable on this UI.
//
// API is value-based, not option-object-based, so it swaps in for a native
// <select> without reshaping the parent's state:
//
//   <SearchableSelect
//     label="Service"
//     value={selectedServiceId}                       // raw value
//     options={services.map(s => ({ value: s.id, label: s.service_name }))}
//     onChange={(value) => setSelectedServiceId(value)}
//     placeholder="Search services…"
//   />
import React, { useMemo } from 'react';
import Select from 'react-select';
import styled, { useTheme } from 'styled-components';

const Wrapper = styled.div`
  position: relative;
  margin-bottom: ${(props) => props.theme.spacing(1)};

  /* Floating label, matching components/shared/Select */
  > label {
    position: absolute;
    top: -8px;
    left: 12px;
    z-index: 2;
    background-color: ${(props) => props.theme.colors.surface};
    color: ${(props) => props.theme.colors.inputGray};
    font-size: 13px;
    padding: 0 5px;
    pointer-events: none;
  }
`;

const Required = styled.span`
  color: ${(props) => props.theme.colors.error};
  margin-left: 3px;
`;

const SearchableSelect = ({
  label,
  value,
  options = [],
  onChange,
  placeholder = 'Search…',
  required = false,
  isDisabled = false,
  isClearable = false,
  name,
}) => {
  const theme = useTheme();

  // react-select works in option objects; the parent works in raw values.
  const selected = useMemo(
    () => options.find(o => String(o.value) === String(value)) ?? null,
    [options, value]
  );

  const styles = useMemo(() => ({
    control: (base, state) => ({
      ...base,
      minHeight: '56px',
      backgroundColor: theme.colors.surface,
      borderColor: state.isFocused ? theme.colors.primary : `${theme.colors.inputGray}66`,
      boxShadow: state.isFocused ? `0 0 0 1px ${theme.colors.primary}` : 'none',
      '&:hover': { borderColor: theme.colors.primary },
    }),
    valueContainer: (base) => ({ ...base, padding: '2px 14px' }),
    // The three react-select defaults that made CustomSelect unreadable here:
    singleValue: (base) => ({ ...base, color: theme.colors.textPrimary }),
    input: (base) => ({ ...base, color: theme.colors.textPrimary }),
    menu: (base) => ({
      ...base,
      backgroundColor: theme.colors.surface,
      border: `1px solid ${theme.colors.inputGray}44`,
      zIndex: 3000,   // above Modal's 1100 overlay
    }),
    menuPortal: (base) => ({ ...base, zIndex: 3000 }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? `${theme.colors.primary}33`
        : state.isFocused
          ? `${theme.colors.primary}1A`
          : 'transparent',
      color: theme.colors.textPrimary,
      cursor: 'pointer',
      ':active': { backgroundColor: `${theme.colors.primary}44` },
    }),
    placeholder: (base) => ({ ...base, color: theme.colors.inputGray }),
    dropdownIndicator: (base) => ({ ...base, color: theme.colors.inputGray }),
    clearIndicator: (base) => ({ ...base, color: theme.colors.inputGray }),
    indicatorSeparator: (base) => ({ ...base, backgroundColor: `${theme.colors.inputGray}55` }),
    noOptionsMessage: (base) => ({ ...base, color: theme.colors.textSecondary }),
  }), [theme]);

  return (
    <Wrapper>
      {label && <label>{label}{required && <Required>*</Required>}</label>}
      <Select
        name={name}
        options={options}
        value={selected}
        onChange={(opt) => onChange(opt ? opt.value : '')}
        placeholder={placeholder}
        isDisabled={isDisabled}
        isClearable={isClearable}
        isSearchable
        classNamePrefix="searchable-select"
        styles={styles}
        // Render the menu in a portal so it isn't clipped by the modal's
        // overflow, and can sit above the overlay.
        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        menuPlacement="auto"
        noOptionsMessage={() => 'No matches'}
      />
    </Wrapper>
  );
};

export default SearchableSelect;
